import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useParams } from 'react-router-dom';

import ShareModal from '../../Components/ShareModal';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { useFeedLocation } from '../../hooks/useFeedLocation';
import { ShortsCard } from './ShortsCards';
import { fetchPosts } from '../../services/CommonFunctions';
import { handleFollow, handleLike, handleBookmark } from '../Post/PostCommonApi';
import { request } from '../../Utils/request';

const Shorts: React.FC = () => {
  const dispatch = useDispatch();
  const { id: shortId } = useParams<{ id?: string }>();
  const { shorts: shortsData, currentPage, hasMore, loading } = useSelector((state: RootState) => state.short);
  const user = useSelector((state: RootState) => state.user.user);
  const { postsReelsByLocation, locationForPosts, isLocationLoading } = useFeedLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<HTMLDivElement[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [selectedShortForShare, setSelectedShortForShare] = useState<any>(null);
  const [isMuted, setIsMuted] = useState<boolean>(true); // Global mute state for all videos
  const [sharedShort, setSharedShort] = useState<any | null>(null);
  const [sharedShortLoading, setSharedShortLoading] = useState(false);
  const [sharedShortNotFound, setSharedShortNotFound] = useState(false);
  const isShareShortView = Boolean(shortId);

  const loadMoreShorts = useCallback(() => {
    if (hasMore && !loading) {
      fetchPosts(dispatch, 'video', currentPage + 1, 5, true, postsReelsByLocation, locationForPosts);
    }
  }, [dispatch, currentPage, hasMore, loading, postsReelsByLocation, locationForPosts]);

  useEffect(() => {
    // Wait for location when "Posts by location" is on and we're still fetching it
    if (isShareShortView) return;
    if (postsReelsByLocation && isLocationLoading) return;
    fetchPosts(dispatch, 'video', 1, 5, false, postsReelsByLocation, locationForPosts);
  }, [dispatch, isShareShortView, postsReelsByLocation, locationForPosts, isLocationLoading]);

  useEffect(() => {
    if (!shortId) {
      setSharedShort(null);
      setSharedShortNotFound(false);
      setSharedShortLoading(false);
      return;
    }

    let cancelled = false;
    const getShortById = async () => {
      try {
        setSharedShortLoading(true);
        setSharedShortNotFound(false);
        const response = await request<any>(`/posts/${shortId}`);
        if (cancelled) return;
        const data = response?.data;
        const oneShort = Array.isArray(data) ? data[0] : data;
        if (oneShort?._id || oneShort?.id) {
          setSharedShort(oneShort);
          setActiveIndex(0);
        } else {
          setSharedShort(null);
          setSharedShortNotFound(true);
        }
      } catch (error) {
        if (!cancelled) {
          setSharedShort(null);
          setSharedShortNotFound(true);
        }
      } finally {
        if (!cancelled) setSharedShortLoading(false);
      }
    };

    getShortById();
    return () => {
      cancelled = true;
    };
  }, [shortId]);

  const handleShare = (short: any) => {
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://ifinds.in' 
      : window.location.origin.replace('https://', 'http://');
    const shareUrl = `${baseUrl}/shorts/${short._id}`;
    const shareText = `Check out this short: ${short.description || short.title || 'Amazing video'}`;
    
    setSelectedShortForShare({
      ...short,
      shareUrl,
      shareText
    });
    setOpenShareModal(true);
  };

  const handleLikeClick = (id: string, isLiked: boolean) => {
    handleLike(id, dispatch, 'video');
  };

  const handleBookmarkClick = (id: string, isSaved: boolean) => {
    handleBookmark(id, dispatch, 'video');
  };

  const handleToggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  useEffect(() => {
    if (isShareShortView) return;
    const handleScroll = () => {
      // Calculate visibility ratio for each video element
      const visibilityRatios = videoRefs.current.map((ref) => {
        if (!ref) return 0;
        const rect = ref.getBoundingClientRect();
        const elementHeight = rect.height || 1; // fallback to prevent division by zero
        const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
        const ratio = visibleHeight / elementHeight;
        return ratio < 0 ? 0 : ratio; // ensure non-negative
      });

      // Find the element that is at least 60% visible and has the highest ratio
      let candidateIndex = activeIndex;
      let highestRatio = 0;

      visibilityRatios.forEach((ratio, idx) => {
        if (ratio >= 0.6 && ratio > highestRatio) {
          highestRatio = ratio;
          candidateIndex = idx;
        }
      });

      if (candidateIndex !== activeIndex) {
        setActiveIndex(candidateIndex);
        
        // Check if we're viewing the second-to-last item and need to load more
        if (candidateIndex >= shortsData.length - 2 && hasMore && !loading) {
          loadMoreShorts();
        }
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [activeIndex, loadMoreShorts, shortsData.length, hasMore, loading, isShareShortView]);

  const renderedShorts = isShareShortView ? (sharedShort ? [sharedShort] : []) : shortsData;

  return (
    <>
      {
        isShareShortView ? (
          sharedShortLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <CircularProgress />
            </Box>
          ) : renderedShorts.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <Typography variant="h6">{sharedShortNotFound ? 'Short not found' : 'No shorts found'}</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                height: 'calc(100vh - 56px)',
                overflowY: 'hidden',
                pb: { xs: '56px', sm: 0 },
              }}
            >
              {renderedShorts.map((item) => (
                <Box key={item._id || item.id} data-short-id={item._id || item.id}>
                  <ShortsCard
                    item={item}
                    index={0}
                    videoRefs={videoRefs}
                    activeIndex={0}
                    handleFollow={() => handleFollow(item?.author?._id, item?._id, dispatch, 'video')}
                    handleShare={() => handleShare(item)}
                    isPreview={false}
                    currentUser={user}
                    onLike={handleLikeClick}
                    onBookmark={handleBookmarkClick}
                    isMuted={isMuted}
                    onToggleMute={handleToggleMute}
                  />
                </Box>
              ))}

              <ShareModal
                open={openShareModal}
                onClose={() => {
                  setOpenShareModal(false);
                  setSelectedShortForShare(null);
                }}
                title={selectedShortForShare?.description || selectedShortForShare?.title || 'Short Video'}
                shareUrl={selectedShortForShare?.shareUrl || ''}
                shareText={selectedShortForShare?.shareText || ''}
              />
            </Box>
          )
        ) : shortsData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <Typography variant="h6">No shorts found</Typography>
          </Box>
        ) : (


          <Box
            ref={scrollContainerRef}
            sx={{
              height: 'calc(100vh - 56px)',
              overflowY: 'scroll',
              scrollSnapType: 'y mandatory',
              pb: { xs: '56px', sm: 0 },
            }}
          >
            {shortsData.map((item, index) => (
              <Box key={item._id} data-short-id={item._id}>
                <ShortsCard
                  item={item}
                  index={index}
                  videoRefs={videoRefs}
                  activeIndex={activeIndex}
                  handleFollow={() => handleFollow(item?.author?._id, item?._id, dispatch, 'video')}
                  handleShare={() => handleShare(item)}
                  isPreview={false}
                  currentUser={user}
                  onLike={handleLikeClick}
                  onBookmark={handleBookmarkClick}
                  isMuted={isMuted}
                  onToggleMute={handleToggleMute}
                />
              </Box>
            ))}
            
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, height: '100vh' }}>
                <CircularProgress />
              </Box>
            )}
            
            {!hasMore && shortsData.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, height: '100vh' }}>
                <Typography variant="body2" color="text.secondary">
                  No more shorts to load
                </Typography>
              </Box>
            )}

            <ShareModal
              open={openShareModal}
              onClose={() => {
                setOpenShareModal(false);
                setSelectedShortForShare(null);
              }}
              title={selectedShortForShare?.description || selectedShortForShare?.title || 'Short Video'}
              shareUrl={selectedShortForShare?.shareUrl || ''}
              shareText={selectedShortForShare?.shareText || ''}
            />
          </Box>
        )
      }
    </>
  );
};





export default Shorts;
